/**
 * Types generated from the API's OpenAPI document.
 *
 * `schema.d.ts` is generated — never edit it. Regenerate with
 * `pnpm --filter @nexus/contracts generate` after the API's contract changes,
 * and the frontend stops compiling wherever it relied on the old shape. That
 * failure is the point: a renamed field should break the build rather than
 * arrive as `undefined` at runtime.
 *
 * The aliases below exist because the generated tree is unpleasant to write by
 * hand — `components['schemas']['StartupSummaryDto']` at every call site is
 * noise, and a typo in it produces `any` rather than an error.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.11
 */
import type { components, operations, paths } from './schema';

export type { components, operations, paths };

/** Every schema the API publishes. */
type Schemas = components['schemas'];

// ── Authentication ──
export type LoginRequest = Schemas['LoginDto'];
export type AuthResponse = Schemas['AuthResponseDto'];
export type SessionUser = Schemas['SessionUserDto'];
export type InviteUserRequest = Schemas['InviteUserDto'];
export type AcceptInvitationRequest = Schemas['AcceptInvitationDto'];

// ── Startups ──
export type StartupSummary = Schemas['StartupSummaryDto'];
export type CreateStartupRequest = Schemas['CreateStartupDto'];
export type UpdateStartupRequest = Schemas['UpdateStartupDto'];
export type ChangeStageRequest = Schemas['ChangeStageDto'];

// ── Scorecard (PRD §5.2) ──
export type Scorecard = Schemas['ScorecardDto'];
export type SaveScorecardRequest = Schemas['SaveScorecardDto'];

// ── Activity log (PRD §5.8) ──
export type Activity = Schemas['ActivityDto'];
export type CreateActivityRequest = Schemas['CreateActivityDto'];
export type UpdateActivityRequest = Schemas['UpdateActivityDto'];

/** Query parameters a record's timeline accepts. */
export type ListActivitiesQuery = NonNullable<
  operations['listActivities']['parameters']['query']
>;

// ── Pilots (PRD §5.4) ──
export type PilotSummary = Schemas['PilotSummaryDto'];
export type PilotDetail = Schemas['PilotDetailDto'];
export type CreatePilotRequest = Schemas['CreatePilotDto'];
export type UpdatePilotRequest = Schemas['UpdatePilotDto'];
export type ReplaceKpisRequest = Schemas['ReplaceKpisDto'];
export type ReplaceMilestonesRequest = Schemas['ReplaceMilestonesDto'];
export type CreateBlockerRequest = Schemas['CreateBlockerDto'];
export type ResolveBlockerRequest = Schemas['ResolveBlockerDto'];

/** One row of the KPI table — PRD §5.4's centrepiece. */
export type PilotKpi = PilotDetail['kpis'][number];

/** Portfolio timeline / Gantt (F16). */
export type PilotTimeline = Schemas['PilotTimelineDto'];
export type TimelineBar = PilotTimeline['bars'][number];
export type UnscheduledPilot = PilotTimeline['unscheduled'][number];
export type PilotMilestone = PilotDetail['milestones'][number];
export type PilotBlocker = PilotDetail['blockers'][number];

// ── Customers (PRD §5.5) ──
export type CustomerSummary = Schemas['CustomerSummaryDto'];
export type CustomerDetail = Schemas['CustomerDetailDto'];
export type CreateCustomerRequest = Schemas['CreateCustomerDto'];
export type UpdateCustomerRequest = Schemas['UpdateCustomerDto'];

// ── Assets (PRD §5.6) ──
export type AssetSummary = Schemas['AssetSummaryDto'];
export type AssetDetail = Schemas['AssetDetailDto'];
export type CreateAssetRequest = Schemas['CreateAssetDto'];
export type UpdateAssetRequest = Schemas['UpdateAssetDto'];

/** Asset map (F19). */
export type AssetMap = Schemas['AssetMapDto'];
export type MapPoint = AssetMap['points'][number];
export type UnchartedAsset = AssetMap['uncharted'][number];
export type AssetCountryCount = AssetMap['byCountry'][number];

// ── Contacts (PRD §5.7, §5.9) ──
export type ContactSummary = Schemas['ContactSummaryDto'];
export type ContactDetail = Schemas['ContactDetailDto'];
export type CreateContactRequest = Schemas['CreateContactDto'];
export type UpdateContactRequest = Schemas['UpdateContactDto'];

/** Which kind of organisation a contact belongs to. */
export type OrgType = ContactSummary['orgType'];

// ── Attachments (PRD §5.10) ──
export type AttachmentSummary = Schemas['AttachmentSummaryDto'];
export type UploadAttachmentRequest = Schemas['UploadAttachmentDto'];
export type UpdateAttachmentRequest = Schemas['UpdateAttachmentDto'];
export type DownloadLink = Schemas['DownloadLinkDto'];

/** What kind of document an attachment is. */
export type AttachmentType = AttachmentSummary['type'];

/**
 * The confidentiality LABEL.
 *
 * Not access control (PRD §5.10): everything sits behind one login, so this
 * changes how a file is presented and nothing about who can read it. The
 * interface must say so explicitly.
 */
export type Confidentiality = AttachmentSummary['confidentiality'];

// ── Legal & finance (PRD §5.11) ──
// No bank details, ever — the schema has no IBAN, BIC or account field.
export type LegalFinance = Schemas['LegalFinanceDto'];
export type SaveLegalFinanceRequest = Schemas['SaveLegalFinanceDto'];

/** Where a supplier onboarding has got to. */
export type SupplierOnboardingStatus = LegalFinance['supplierOnboardingStatus'];

// ── Global search (F6) ──
export type SearchResults = Schemas['SearchResultsDto'];

/**
 * Derived from the results rather than named separately.
 *
 * `SearchHitDto` and `SearchGroupDto` are declared on the server but only
 * ever appear nested inside `SearchResultsDto`, so `nestjs-zod` inlines them
 * and emits no standalone schema. Indexing the structure keeps these types
 * tied to what the endpoint actually returns.
 */
export type SearchGroup = SearchResults['groups'][number];
export type SearchHit = SearchGroup['hits'][number];

/** Which kind of record a hit is. */
export type SearchEntity = SearchGroup['entity'];

/** Query parameters the list endpoint accepts. */
export type ListStartupsQuery = NonNullable<
  operations['listStartups']['parameters']['query']
>;

export type ListPilotsQuery = NonNullable<
  operations['listPilots']['parameters']['query']
>;

export type ListCustomersQuery = NonNullable<
  operations['listCustomers']['parameters']['query']
>;

export type ListAssetsQuery = NonNullable<
  operations['listAssets']['parameters']['query']
>;

export type ListContactsQuery = NonNullable<
  operations['listContacts']['parameters']['query']
>;

export type ListAttachmentsQuery = NonNullable<
  operations['listAttachments']['parameters']['query']
>;

export type SearchQuery = NonNullable<operations['search']['parameters']['query']>;

export type MatchmakerQuery = NonNullable<
  operations['matchStartupToAssets']['parameters']['query']
>;

export type ListTaxonomyQuery = NonNullable<
  operations['listTaxonomy']['parameters']['query']
>;

// ── Dashboard (F10) ──
export type Dashboard = Schemas['DashboardDto'];

/** One stage in the funnel, and one row in each of the dashboard's lists. */
export type FunnelStage = Dashboard['funnel'][number];
export type OverdueAction = Dashboard['overdueActions'][number];
export type ActivePilot = Dashboard['activePilots'][number];
export type RecentActivity = Dashboard['recentActivity'][number];

// ── Analytics (F15) ──
export type Analytics = Schemas['AnalyticsDto'];

/** Derived rows of the analytics payload, tied to what the endpoint returns. */
export type StageConversion = Analytics['conversion'][number];
export type StageDuration = Analytics['stageDurations'][number];
export type AnalyticsNamedCount = Analytics['technologyMix'][number];
export type SourceEffectiveness = Analytics['sourceEffectiveness'][number];

// ── Matchmaker (F11) ──
export type MatchmakerResult = Schemas['MatchmakerResultDto'];
export type AssetMatch = MatchmakerResult['matches'][number];

// ── Alerts (F12) ──
export type AlertsResult = Schemas['AlertsResultDto'];

/**
 * One alert, derived from the result rather than named separately.
 *
 * `AlertDto` is declared on the server but only ever appears nested inside
 * `AlertsResultDto`, so `nestjs-zod` inlines it and emits no standalone schema
 * — the same treatment `SearchHit` gets. Indexing keeps the type tied to what
 * the endpoint returns.
 */
export type Alert = AlertsResult['alerts'][number];

/** Which of the five F12 signals an alert is. */
export type AlertKind = Alert['kind'];
export type AlertSeverity = Alert['severity'];
export type AlertEntity = Alert['entityType'];

// ── Duplicate detection (F14) ──
export type DuplicateMatch = Schemas['DuplicateMatchDto'];

/** How firmly a duplicate is flagged. */
export type DuplicateConfidence = DuplicateMatch['confidence'];

export type CheckDuplicatesQuery = NonNullable<
  operations['checkStartupDuplicates']['parameters']['query']
>;

// ── Admin (F9) ──
export type TaxonomyTerm = Schemas['TaxonomyTermDto'];
export type CreateTaxonomyRequest = Schemas['CreateTaxonomyDto'];
export type UpdateTaxonomyRequest = Schemas['UpdateTaxonomyDto'];
export type AdminUser = Schemas['AdminUserDto'];
export type AdminSettings = Schemas['AdminSettingsDto'];
export type SaveScorecardCriteriaRequest = Schemas['SaveScorecardCriteriaDto'];
export type SaveBandThresholdsRequest = Schemas['SaveBandThresholdsDto'];
export type SaveStallThresholdsRequest = Schemas['SaveStallThresholdsDto'];

/** The kind of a taxonomy vocabulary. */
export type TaxonomyKind = TaxonomyTerm['kind'];

/** A user's derived status in the admin panel. */
export type AdminUserStatus = AdminUser['status'];

/**
 * The roles of PRD §4.
 *
 * Declared here rather than derived from the schema: the OpenAPI document
 * describes it as a bare string, and the UI needs the union to check a role
 * exhaustively.
 */
export const ROLES = ['ADMIN', 'EDITOR', 'VIEWER'] as const;

export type Role = (typeof ROLES)[number];
